import { c as _c } from "react/compiler-runtime";
// `<OgOutcomeChips>` — flat-wrap row of coloured-dot + label chips, one
// per outcome. Used by the proposal OG card where we want to show every
// outcome name without per-outcome bars.
//
// Each chip = circle swatch (16px, hashed colour from `getOutcomeColorOg`)
// + label (28px). The container wraps so high outcome counts flow to a
// second or third row. When `chips.length > maxVisible`, the tail
// collapses into a neutral "+N" chip.
//
// Long labels: each chip clamps its label to `maxChipLabelPx` (auto-tuned
// down for high-density rows) using `-webkit-line-clamp: 1` + ellipsis.
// `flexShrink: 0` on every chip root means the wrap container never
// squeezes a chip below its intrinsic clamped width — chips always wrap
// to a new row instead of shrinking.
//
// Satori-friendly: flex wrap is supported; every node sets explicit
// `display: 'flex'`. No nested elements inside text spans.

import type { CSSProperties } from 'react';
import { OG_CONTENT_WIDTH, OG_TOKENS } from '../tokens';
export interface OgOutcomeChip {
  /** Outcome label (sanitised by the caller). */
  label: string;
  /** Hex colour for the swatch — typically from `getOutcomeColorsOg`. */
  color: string;
}
export interface OgOutcomeChipsProps {
  /** Already-sorted list of outcome chips (caller decides ordering). */
  chips: readonly OgOutcomeChip[];
  /** Max chips to render before collapsing to "+N more". Defaults to 8. */
  maxVisible?: number;
  /** Container width in px. Default `OG_CONTENT_WIDTH` (matches funding bar / row). */
  width?: number;
  /** Per-chip max label width in px. When omitted, the primitive auto-
   *  tunes based on `chips.length` so dense rows don't blow the layout
   *  with long labels. Override only when the caller has stronger
   *  knowledge of the label distribution. */
  maxChipLabelPx?: number;
}
const SWATCH_PX = 16;
const CHIP_GAP_X = 28;
const CHIP_GAP_Y = 12;

/** Auto-tune the per-chip label cap so dense rows still fit two-up.
 *  Heuristic: the wider container is `OG_CONTENT_WIDTH` (1072px), and each chip's intrinsic
 *  width is `swatch + gap + maxLabel ≈ 26 + maxLabel`. We aim for ≥3
 *  chips per row at the worst case to limit total rows to 3 — beyond
 *  that the card starts encroaching on the footer. */
function defaultLabelPx(chipCount: number): number {
  if (chipCount <= 6) return 280;
  if (chipCount <= 9) return 200;
  return 160;
}

/** Overflow-chip text. For low counts we say "+N more" (reads well in
 *  feed thumbnails); for dense rows we use just "+N" so the overflow
 *  chip itself stays narrow and doesn't push real outcome names to a
 *  new row. */
function overflowText(hiddenCount: number, totalCount: number): string {
  const n = hiddenCount.toString();
  return totalCount > 6 ? `+${n}` : `+${n} more`;
}
export function OgOutcomeChips(props) {
  const $ = _c(23);
  const maxVisible = props.maxVisible ?? 8;
  const width = props.width ?? OG_CONTENT_WIDTH;
  const total = props.chips.length;
  let t0;
  if ($[0] !== props.maxChipLabelPx || $[1] !== total) {
    t0 = props.maxChipLabelPx ?? defaultLabelPx(total);
    $[0] = props.maxChipLabelPx;
    $[1] = total;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const maxLabelPx = t0;
  const overflow = total > maxVisible;
  const visibleCount = overflow ? maxVisible - 1 : total;
  let hiddenCount;
  let t1;
  let t2;
  if ($[3] !== maxLabelPx || $[4] !== props.chips || $[5] !== total || $[6] !== visibleCount || $[7] !== width) {
    const visible = props.chips.slice(0, visibleCount);
    hiddenCount = total - visibleCount;
    let t3;
    if ($[11] !== width) {
      t3 = {
        display: "flex",
        flexWrap: "wrap",
        width,
        columnGap: CHIP_GAP_X,
        rowGap: CHIP_GAP_Y
      };
      $[11] = width;
      $[12] = t3;
    } else {
      t3 = $[12];
    }
    const containerStyle = t3;
    t1 = containerStyle;
    let t4;
    if ($[13] !== maxLabelPx) {
      t4 = chip => <div key={chip.label} style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        flexShrink: 0
      }}><div style={{
          display: "flex",
          width: SWATCH_PX,
          height: SWATCH_PX,
          borderRadius: SWATCH_PX / 2,
          background: chip.color,
          flexShrink: 0
        }} /><div style={{
          display: "-webkit-box",
          WebkitBoxOrient: "vertical",
          WebkitLineClamp: 1,
          overflow: "hidden",
          textOverflow: "ellipsis",
          fontSize: 28,
          fontWeight: 600,
          color: OG_TOKENS.gray100,
          maxWidth: maxLabelPx,
          lineHeight: 1.1
        }}>{chip.label}</div></div>;
      $[13] = maxLabelPx;
      $[14] = t4;
    } else {
      t4 = $[14];
    }
    t2 = visible.map(t4);
    $[3] = maxLabelPx;
    $[4] = props.chips;
    $[5] = total;
    $[6] = visibleCount;
    $[7] = width;
    $[8] = hiddenCount;
    $[9] = t1;
    $[10] = t2;
  } else {
    hiddenCount = $[8];
    t1 = $[9];
    t2 = $[10];
  }
  let t3;
  if ($[15] !== hiddenCount || $[16] !== overflow || $[17] !== total) {
    t3 = overflow && <div style={{
      display: "flex",
      alignItems: "center",
      gap: 10,
      flexShrink: 0
    }}><div style={{
        display: "flex",
        width: SWATCH_PX,
        height: SWATCH_PX,
        borderRadius: SWATCH_PX / 2,
        background: OG_TOKENS.gray500,
        opacity: 0.6,
        flexShrink: 0
      }} /><span style={{
        display: "flex",
        fontSize: 28,
        fontWeight: 500,
        color: OG_TOKENS.gray100,
        opacity: 0.7
      }}>{overflowText(hiddenCount, total)}</span></div>;
    $[15] = hiddenCount;
    $[16] = overflow;
    $[17] = total;
    $[18] = t3;
  } else {
    t3 = $[18];
  }
  let t4;
  if ($[19] !== t1 || $[20] !== t2 || $[21] !== t3) {
    t4 = <div style={t1}>{t2}{t3}</div>;
    $[19] = t1;
    $[20] = t2;
    $[21] = t3;
    $[22] = t4;
  } else {
    t4 = $[22];
  }
  return t4;
}